import React, {useState, useRef, useEffect} from "react";
import {crud} from "../../crudRequests";
import {
  Box,
  Button,
  Flex,
  Heading,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  FormErrorMessage,

} from "@chakra-ui/react";
import {normalizeDateInput, toInputDateValue} from "../../utils/dateValidation";
import {
  getUnitPrefs,
  setUnitPrefs,
  cmFromFeetIn,
  feetInFromCm,
  kgFromLbs,
  lbsFromKg,
} from "../../utils/unitConversion";
import {isValidCanadianPostalCode} from "../../utils/postalCodeValidation";
import {sanitizeWeightInput} from "../../utils/weightValidation";
import {provinceOptions, countryOptions} from "../../utils/locationOptions";
export default function PatientInfo({state, dispatch}) {

  const p = state.patient;
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [dobError, setDobError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");

  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const dobRef = useRef();
  const genderRef = useRef();
  const cityRef = useRef();
  const emailRef = useRef();
  const provinceRef = useRef();
  const countryRef = useRef();
  const middleNameRef = useRef();
  const preferredNameRef = useRef();
  const preferredPronounRef = useRef();
  const emergencyContactNameRef = useRef();
  const emergencyContactPhoneRef = useRef();
  const emergencyContactRelationRef = useRef();
  const address1Ref = useRef();
  const address2Ref = useRef();
  const postalCodeRef = useRef();
  const phoneCellRef = useRef();
  const phoneAltRef = useRef();
  const homePhoneRef = useRef();
  const emergencyContactEmailRef = useRef();
  const [heightUnit, setHeightUnit] = useState(() => getUnitPrefs().heightUnit);
  const [weightUnit, setWeightUnit] = useState(() => getUnitPrefs().weightUnit);
  const [heightFields, setHeightFields] = useState({cm: p["height"] || "", feet: "", inches: ""});
  const [weightField, setWeightField] = useState(p["weight"] || "");

  const formDirtyRef = useRef(false);

  const syncRefValue = (ref, value) => {
    if (!ref.current) return;
    if (document.activeElement === ref.current) return;
    // Skip sync if the user has modified this field from its original value
    const currentVal = ref.current.value ?? "";
    const newVal = value ?? "";
    if (currentVal !== newVal && formDirtyRef.current) return;
    ref.current.value = newVal;
  };

  const formatWeightFieldForUnit = (weightValue, unit) => {
    if (weightValue === undefined || weightValue === null || weightValue === "") {
      return "";
    }
    const converted = unit === "lbs" ? lbsFromKg(weightValue) : weightValue;
    return sanitizeWeightInput(converted);
  };

  const handleWeightFieldChange = (value) => {
    const sanitized = sanitizeWeightInput(value);
    setWeightField(sanitized);
  };

  useEffect(() => {
    const prefs = getUnitPrefs();
    setHeightUnit(prefs.heightUnit);
    setWeightUnit(prefs.weightUnit);
  }, []);

  useEffect(() => {
    if (!p) return;
    syncRefValue(firstNameRef, p.fName);
    syncRefValue(lastNameRef, p.lName);
    syncRefValue(dobRef, toInputDateValue(p.dob));
    syncRefValue(genderRef, p.gender);
    syncRefValue(cityRef, p.city);
    syncRefValue(emailRef, p.email);
    syncRefValue(provinceRef, p.province);
    syncRefValue(countryRef, p.country);
    syncRefValue(middleNameRef, p.mName);
    syncRefValue(preferredNameRef, p.preferredName);
    syncRefValue(preferredPronounRef, p.preferredPronoun);
    syncRefValue(emergencyContactNameRef, p.emergencyContactName);
    syncRefValue(emergencyContactPhoneRef, p.emergencyContactPhone);
    syncRefValue(emergencyContactRelationRef, p.emergencyContactRelation);
    syncRefValue(address1Ref, p.address1);
    syncRefValue(address2Ref, p.address2);
    syncRefValue(postalCodeRef, p.postalCode);
    syncRefValue(phoneCellRef, p.phone);
    syncRefValue(phoneAltRef, p.alternativePhone);
    syncRefValue(homePhoneRef, p.homePhone);
    syncRefValue(emergencyContactEmailRef, p.emergencyContactEmail);
  }, [p]);

  useEffect(() => {
    const cmVal = p["height"] || "";
    if (cmVal) {
      const {feet, inches} = feetInFromCm(cmVal);
      setHeightFields({cm: cmVal, feet, inches});
    } else {
      setHeightFields({cm: "", feet: "", inches: ""});
    }
    const kgVal = p["weight"] || "";
    if (kgVal !== "") {
      const converted = weightUnit === "lbs" ? lbsFromKg(kgVal) : kgVal;
      setWeightField(sanitizeWeightInput(converted));
    } else {
      setWeightField("");
    }
  }, [p, weightUnit]);

  async function updatePatientInformation() {
    const postalCodeValue = postalCodeRef.current?.value?.trim() || "";
    if (postalCodeValue && !isValidCanadianPostalCode(postalCodeValue)) {
      setPostalCodeError("Enter a valid Canadian postal code (A0A 0A0).");
      return;
    }
    setPostalCodeError("");
    const parsedDob = normalizeDateInput(dobRef.current.value);
    if (!parsedDob.isValid) {
      setDobError(parsedDob.error);
      toast({
        title: "Invalid date of birth",
        description: parsedDob.error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setDobError("");
    const heightMetric =
      heightUnit === "ft-in"
        ? cmFromFeetIn(heightFields.feet, heightFields.inches)
        : Number(heightFields.cm);
    const sanitizedWeightField = sanitizeWeightInput(weightField);
    setWeightField(sanitizedWeightField);
    const weightMetric =
      sanitizedWeightField === ""
        ? null
        : weightUnit === "lbs"
        ? kgFromLbs(Number(sanitizedWeightField))
        : Number(sanitizedWeightField);
    setLoading(true);
    let payload = {
      pid: p["pid"],
      fName: firstNameRef.current.value,
      lName: lastNameRef.current.value,
      dob: parsedDob.canonical,
      gender: genderRef.current.value,
      height: heightMetric,
      weight: weightMetric,
      city: cityRef.current.value,
      email: emailRef.current.value,
      phone: phoneCellRef.current?.value,
      province: provinceRef.current.value,
      postalCode: postalCodeRef.current?.value,
      country: countryRef.current.value,
      mName: middleNameRef.current.value,
      preferredName: preferredNameRef.current.value,
      preferredPronoun: preferredPronounRef.current.value,
      emergencyContactName: emergencyContactNameRef.current.value,
      emergencyContactPhone: emergencyContactPhoneRef.current.value,
      emergencyContactRelation: emergencyContactRelationRef.current.value,
      address1: address1Ref.current.value,
      address2: address2Ref.current.value,
      alternativePhone: phoneAltRef.current.value,
      homePhone: homePhoneRef.current.value,
      emergencyContactEmail: emergencyContactEmailRef.current.value,
    };
    crud(state, [
      {
        db: state.db,
        collection: "patients",
        parameters: [{pid: p["pid"]}, {$set: payload}],
        method: "updateOne",
      },
    ])
      .then(() => {
        setLoading(false);
        formDirtyRef.current = false;
        dispatch({
          type: "UPDATE_PATIENT",
          pid: p.pid,
          payload,
        });
        toast({
          title: "Saved",
          description: "Profile updated successfully",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch(function (error) {
        setLoading(false);
        toast({
          title: "Update failed",
          description: "Please try again",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }

  return (

    <Box w="full" px={0} py={2}>
   
      <form
        onSubmit={(e) => {
          e.preventDefault();
          updatePatientInformation();
        }}
        onInput={() => { formDirtyRef.current = true; }}
        onChange={() => { formDirtyRef.current = true; }}
      >
        <Box
          bg="gray.50"
          borderRadius="12px"
          p={{base: 4, md: 5}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Personal details
          </Heading>
          <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
            <FormControl isRequired>
              <FormLabel>First name</FormLabel>
              <Input ref={firstNameRef} defaultValue={p["fName"]} id="fName" />
            </FormControl>
            <FormControl>
              <FormLabel>Middle name</FormLabel>
              <Input ref={middleNameRef} defaultValue={p["mName"]} id="mName" />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Last name</FormLabel>
              <Input ref={lastNameRef} defaultValue={p["lName"]} id="lName" />
            </FormControl>
            <FormControl>
              <FormLabel>Preferred name</FormLabel>
              <Input
                ref={preferredNameRef}
                defaultValue={p["preferredName"]}
                id="preferredName"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Preferred pronoun</FormLabel>
              <Select
                ref={preferredPronounRef}
                defaultValue={p["preferredPronoun"]}
                id="preferredPronoun"
              >
                <option value="">Select</option>
                <option value="he/him">He/Him</option>
                <option value="she/her">She/Her</option>
                <option value="they/them">They/Them</option>
              </Select>
            </FormControl>
            <FormControl isInvalid={!!dobError}>
              <FormLabel>Date of birth</FormLabel>
              <Input
                ref={dobRef}
                defaultValue={toInputDateValue(p["dob"])}
                id="dob"
                type="date"
                min="1900-01-01"
                max={new Date().toISOString().split("T")[0]}
                onFocus={(e) => {
                  try {
                    e.target.showPicker?.();
                  } catch (_error) {
                    // Some browsers do not support programmatic date pickers.
                  }
                }}
                onChange={(e) => {
                  const parsed = normalizeDateInput(e.target.value);
                  if (parsed.isValid && e.target.value > new Date().toISOString().split("T")[0]) {
                    setDobError("Date of birth cannot be in the future");
                  } else {
                    setDobError(parsed.isValid ? "" : parsed.error);
                  }
                }}
              />
              {dobError && <FormErrorMessage>{dobError}</FormErrorMessage>}
            </FormControl>
            <FormControl>
              <FormLabel>Gender</FormLabel>
              <Input
                ref={genderRef}
                defaultValue={p["gender"]}
                id="gender"
                placeholder="Enter gender"
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          p={{base: 4, md: 5}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Contact
          </Heading>
          <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                ref={emailRef}
                defaultValue={p["email"]}
                id="email"
                type="email"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone (cell)</FormLabel>
              <Input
                ref={phoneCellRef}
                defaultValue={p["phone"]}
                id="phoneCell"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone (alternative)</FormLabel>
              <Input
                ref={phoneAltRef}
                defaultValue={p["alternativePhone"]}
                id="phoneAlt"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Home phone</FormLabel>
              <Input
                ref={homePhoneRef}
                defaultValue={p["homePhone"]}
                id="homePhone"
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          p={{base: 4, md: 5}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Address
          </Heading>
          <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
            <FormControl>
              <FormLabel>Address line 1</FormLabel>
              <Input
                ref={address1Ref}
                defaultValue={p["address1"]}
                id="address1"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Address line 2</FormLabel>
              <Input
                ref={address2Ref}
                defaultValue={p["address2"]}
                id="address2"
              />
            </FormControl>
            <FormControl>
              <FormLabel>City</FormLabel>
              <Input ref={cityRef} defaultValue={p["city"]} id="city" />
            </FormControl>
            <FormControl>
              <FormLabel>Province/State</FormLabel>
              <Select
                ref={provinceRef}
                defaultValue={p["province"] || ""}
                id="province"
                placeholder="Select province"
              >
                {provinceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl isInvalid={!!postalCodeError}>
              <FormLabel>Postal code</FormLabel>
              <Input
                ref={postalCodeRef}
                defaultValue={p["postalCode"]}
                id="postalCode"
                onChange={() => {
                  if (postalCodeError) {
                    setPostalCodeError("");
                  }
                }}
              />
              {postalCodeError && (
                <FormErrorMessage>{postalCodeError}</FormErrorMessage>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Country</FormLabel>
              <Select
                ref={countryRef}
                defaultValue={p["country"] || ""}
                id="country"
                placeholder="Select country"
              >
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          p={{base: 4, md: 5}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Emergency contact
          </Heading>
          <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                ref={emergencyContactNameRef}
                defaultValue={p["emergencyContactName"]}
                id="emergencyContactName"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Relation</FormLabel>
              <Input
                ref={emergencyContactRelationRef}
                defaultValue={p["emergencyContactRelation"]}
                id="emergencyContactRelation"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                ref={emergencyContactEmailRef}
                defaultValue={p["emergencyContactEmail"]}
                id="emergencyContactEmail"
                type="email"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                ref={emergencyContactPhoneRef}
                defaultValue={p["emergencyContactPhone"]}
                id="emergencyContactPhone"
              />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          p={{base: 4, md: 5}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Vitals
          </Heading>
          <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
            <FormControl>
              <FormLabel>Height</FormLabel>
              <Flex gap={2} align="center">
                <Select
                  value={heightUnit}
                  onChange={(e) => {
                    const unit = e.target.value;
                    setHeightUnit(unit);
                    setUnitPrefs({heightUnit: unit});
                    if (p["height"]) {
                      if (unit === "ft-in") {
                        const {feet, inches} = feetInFromCm(p["height"]);
                        setHeightFields((prev) => ({...prev, feet, inches}));
                      } else {
                        setHeightFields((prev) => ({...prev, cm: p["height"]}));
                      }
                    }
                  }}
                  w="32"
                >
                  <option value="cm">cm</option>
                  <option value="ft-in">ft / in</option>
                </Select>
                {heightUnit === "cm" ? (
                  <Input
                    value={heightFields.cm}
                    onChange={(e) => setHeightFields((prev) => ({...prev, cm: e.target.value}))}
                    id="height"
                    type="number"
                    placeholder="Height (cm)"
                  />
                ) : (
                  <Flex gap={2} align="center">
                    <Input
                      value={heightFields.feet}
                      onChange={(e) => setHeightFields((prev) => ({...prev, feet: e.target.value}))}
                      id="height-feet"
                      type="number"
                      placeholder="ft"
                      w="24"
                    />
                    <Input
                      value={heightFields.inches}
                      onChange={(e) => setHeightFields((prev) => ({...prev, inches: e.target.value}))}
                      id="height-inches"
                      type="number"
                      placeholder="in"
                      w="24"
                    />
                  </Flex>
                )}
              </Flex>
            </FormControl>
            <FormControl>
              <FormLabel>Weight</FormLabel>
              <Flex gap={2} align="center">
                <Select
                  value={weightUnit}
                  onChange={(e) => {
                    const unit = e.target.value;
                    setWeightUnit(unit);
                    setUnitPrefs({weightUnit: unit});
                    setWeightField(formatWeightFieldForUnit(p["weight"], unit));
                  }}
                  w="32"
                >
                  <option value="kg">kg</option>
                  <option value="lbs">lbs</option>
                </Select>
                <Input
                  value={weightField}
                  onChange={(e) => handleWeightFieldChange(e.target.value)}
                  id="weight"
                  type="number"
                  placeholder={weightUnit === "lbs" ? "Weight (lbs)" : "Weight (kg)"}
                />
              </Flex>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Button
          type="submit"
          isLoading={loading}
          isDisabled={loading}
          variant="outline"
          borderColor="gray.300"
          bg="white"
          _hover={{bg: "#f8f9fa"}}
          my={3}
          px="8"
          size="sm"
          display="block"
          mx="auto"
        >
          Save
        </Button>
      </form>
    </Box>
  );
}
